import { Button } from '@techem/ui-components';
import checkmarkIcon from 'presentation/assets/icons/png/3x/circle-check-info@3x.png';
import invoiceIcon from 'presentation/assets/icons/png/3x/icn-abrechnung-fertig@3x-schwarz.png';
import screenIcon from 'presentation/assets/icons/png/3x/icn-bildschirm@3x-schwarz.png';
import houseTransmissionIcon from 'presentation/assets/icons/png/3x/icn-datenuebermittlung@3x-schwarz.png';
import radiatorIcon from 'presentation/assets/icons/png/3x/icn-heizung@3x-schwarz.png';
import deviceIcon from 'presentation/assets/icons/png/3x/status-display@3x.png';
import calendarIcon from 'presentation/assets/icons/svg/calendar.svg';
import { useStyletron } from 'baseui';
import { SIZE } from 'baseui/button';
import CenteredLoadingIndicator from 'presentation/components/CenteredLoadingIndicator';
import Icon, { ICONSIZE } from 'presentation/components/Icon';
import TitleWithPrevPageLink from 'presentation/components/TitleWithPrevPageLink';
import { WizardStepsContainer } from 'presentation/components/WizardSteps';
import React, { useEffect } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ProductService } from 'frameworks_drivers/redux/reducers/onboardingInformation';
import { translationKeyForCheckboxOption } from 'tools/CheckboxOptionMapper';
import { useMediaQuery } from 'tools/hooks/useMediaQuery';
import { summaryTranslationKeyForRadioOption } from 'tools/RadioOptionMapper';
import TableEntry from '../Flow/Summary/component/TableEntry';
import useConfirmOfferController from './useConfirmOfferController';
import { Products } from '../../../entities/model/Product';
import { useAcceptDataProcessingTerms } from '../../../interface_adapters/hooks/useAcceptDataProcessingTerms';
import AcceptDataProcessingTermsCheckbox from '../../../frameworks_drivers/components/AcceptDataProcessingTermsCheckbox';
import { useAppDispatch } from '../../../store';
import { DataProcessingAgreementApiService } from '../../../interface_adapters/services/dataProcessingAgreement/DataProcessingAgreementApiService';
import { setDataProcessingAgreement } from '../../../frameworks_drivers/redux/reducers/miscellaneous/miscSlice';
import { formatBillingPeriod } from '../../../interface_adapters/presenters/BillingPeriodPresenter';
import {
  formatPrice,
  formatPriceForAdditionalService
} from '../../../interface_adapters/presenters/PricePresenter';
import { GreyLine } from './components/GreyLine';
import { CenteredSubContent } from './components/CenteredSubContent';
import { PriceTag } from './components/PriceTag';
import { SectionSeparator } from './components/SectionSeparator';
import { Card } from './components/Card';

function SectionTitle(props: any) {
  const [css] = useStyletron();

  return (
    <Row className="justify-content-center">
      <Col xs={12}>
        <h4 className={css({ marginTop: '-24px' }) + ' mb-4'}>{props.title}</h4>
      </Col>
    </Row>
  );
}

function HorizontalLine() {
  return (
    <Row className="justify-content-center">
      <Col xs={12}>
        <GreyLine className="w-100" />
      </Col>
    </Row>
  );
}

const ConfirmOfferPage: React.FC = () => {
  const {
    state,
    actions: { sumPrice }
  } = useConfirmOfferController();
  const { accepted, setAccepted } = useAcceptDataProcessingTerms();
  const isAboveXXS = useMediaQuery('(min-width: 376px)');
  const navigate = useNavigate();
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  if (state.isExpired) {
    navigate({
      pathname: '/offer-expired',
      search: `?offerId=${state.offerId}`
    });
  }

  const handleOrderNowButtonClicked = () => {
    state.convertLeadToOrder();
  };

  const selectedProducts = () => {
    return state.onboardingInfo?.products.map((item: ProductService) => {
      return { name: item.name, price: item.price };
    });
  };

  const getTranslationKeyForService = (serviceName: string) => {
    switch (serviceName) {
      case Products.FullOperatingCosts:
        return 'pageSummaryAdditionalServicesSectionLabelFullOperatingCosts';
      case Products.ColdWaterInvoicing:
        return 'pageSummaryAdditionalServicesSectionLabelWaterConsumption';
      case Products.ProviderChangeService:
        return 'pageSummaryAdditionalServicesSectionLabelProviderChange';
    }
    return '';
  };

  const additionalServicesSection = () => {
    const elements: any[] = [];
    let products = selectedProducts();

    // Remove heating costs as they are the base service, not an additional one!
    products = products!.filter(
      (item: any) => ![Products.HeatingCosts, Products.HotWaterInvoicing].includes(item.name)
    );

    if (products.length === 0) {
      return <></>;
    } else {
      elements.push(
        <SectionSeparator />,
        <SectionTitle title={t('pageSummaryAdditionalServicesSectionTitle')} />,
        <HorizontalLine />
      );
      products.forEach((product: any) => {
        elements.push(
          <TableEntry
            key={'entry_' + product.name}
            rightValues={[t(getTranslationKeyForService(product.name))]}
            endElement={
              <p
                className="mx-0 my-auto fw-bold"
                style={{
                  color: `#007c94!important`
                }}
              >
                {formatPriceForAdditionalService(t, +product.price)}
              </p>
            }
          />
        );
        elements.push(<HorizontalLine key={'line_' + product.name} />);
      });

      return elements;
    }
  };

  const billingPeriodString = formatBillingPeriod(
    state.onboardingInfo?.billingPeriod?.startDate,
    state.onboardingInfo?.billingPeriod?.endDate
  );

  useEffect(() => {
    const dpaService = new DataProcessingAgreementApiService();
    dpaService.loadLatestVersion().then((dataProcessingAgreement) => {
      console.debug('Used data processing agreement: ', dataProcessingAgreement);

      // TODO: Set default value in case of undefined - need to talk to PM/legal first
      if (dataProcessingAgreement) dispatch(setDataProcessingAgreement(dataProcessingAgreement));
    });
  }, [dispatch]);

  return (
    <Container className="pb-4 mb-5">
      <WizardStepsContainer />
      <TitleWithPrevPageLink title={t('pageSummaryTitle')} />

      {state.isLoadingInitialData ? (
        <CenteredLoadingIndicator />
      ) : (
        <Row className="justify-content-center">
          <Col xs={12} md={10} lg={8}>
            <SectionSeparator />

            <SectionTitle title={t('pageSummaryCollectedDataSectionTitle')} />

            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelNumberOfApartments')]}
              rightValues={[
                `${state.onboardingInfo!.numberOfApartments} ${t(
                  'pageSummaryCollectedDataSectionLabelApartments'
                )}`
              ]}
              additionalTestId="apartment-count"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelPreviousInvoice')]}
              rightValues={[
                t(summaryTranslationKeyForRadioOption(state.onboardingInfo!.heatingCostsInvoice))
              ]}
              additionalTestId="thermal-metering"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelCentralHeating')]}
              rightValues={[
                t(
                  summaryTranslationKeyForRadioOption(state.onboardingInfo!.centralHeatingAvailable)
                )
              ]}
              additionalTestId="central-heating"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelHotWaterDelivery')]}
              rightValues={[
                t(
                  summaryTranslationKeyForRadioOption(state.onboardingInfo!.centralHotWaterDelivery)
                )
              ]}
              additionalTestId="hot-water-delivery"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelHeatingType')]}
              rightValues={[
                t(summaryTranslationKeyForRadioOption(state.onboardingInfo!.heatingType))
              ]}
              additionalTestId="heating-type"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelHeatingSources')]}
              rightValues={[
                state
                  .onboardingInfo!.heatingSources.map((heatingSourceKey) => {
                    return t(translationKeyForCheckboxOption(heatingSourceKey));
                  })
                  .join(', ')
              ]}
              additionalTestId="heating-sources"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelContactDetails')]}
              rightValues={[
                t(summaryTranslationKeyForRadioOption(state.onboardingInfo!.contactDetails.gender)),
                state.onboardingInfo?.contactDetails.fname +
                  ' ' +
                  state.onboardingInfo?.contactDetails.lname,
                state.onboardingInfo?.contactDetails.phone,
                state.onboardingInfo?.contactDetails.email
              ]}
              additionalTestId="contact-details"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelPersonalAddress')]}
              rightValues={[
                state.onboardingInfo?.invoiceAddress.street +
                  ' ' +
                  state.onboardingInfo?.invoiceAddress.houseNumber,
                state.onboardingInfo?.invoiceAddress.zip +
                  ' ' +
                  state.onboardingInfo?.invoiceAddress.city
              ]}
              additionalTestId="invoice-address"
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[t('pageSummaryCollectedDataSectionLabelHouseAddress')]}
              rightValues={[
                state.onboardingInfo?.houseAddress.street +
                  ' ' +
                  state.onboardingInfo?.houseAddress.houseNumber,
                state.onboardingInfo?.houseAddress.zip +
                  ' ' +
                  state.onboardingInfo?.houseAddress.city
              ]}
              additionalTestId="house-address"
            />
            <HorizontalLine />
            {state.onboardingInfo!.billingPeriod &&
              state.onboardingInfo!.billingPeriod.startDate && (
                <>
                  <TableEntry
                    leftValues={[t('pageSummaryCollectedDataSectionLabelBillingPeriod')]}
                    rightValues={[billingPeriodString]}
                    additionalTestId="billing-period"
                  />
                  <HorizontalLine />
                </>
              )}

            <SectionSeparator />

            <SectionTitle title={t('pageSummaryBasicServicesSectionTitle')} />

            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelInvoicingAllRentersTitle'),
                t('pageSummaryBasicServicesSectionLabelInvoicingAllRentersContent')
              ]}
              startElement={
                <Icon iconSrc={invoiceIcon} size={ICONSIZE.default} alt="invoice icon"></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.default}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelMeasuringDevicesTitle'),
                t('pageSummaryBasicServicesSectionLabelMeasuringDevicesContent')
              ]}
              startElement={
                <Icon
                  iconSrc={deviceIcon}
                  size={ICONSIZE.default}
                  alt="measuring devices icon"
                ></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.small}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelExactMeteringTitle'),
                t('pageSummaryBasicServicesSectionLabelExactMeteringContent')
              ]}
              startElement={
                <Icon
                  iconSrc={radiatorIcon}
                  size={ICONSIZE.default}
                  alt="exact metering icon"
                ></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.small}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelWirelessConsumptionMeteringTitle'),
                t('pageSummaryBasicServicesSectionLabelWirelessConsumptionMeteringContent')
              ]}
              startElement={
                <Icon
                  iconSrc={houseTransmissionIcon}
                  size={ICONSIZE.default}
                  alt="wireless consumption metering icon"
                ></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.small}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelPriceGuaranteeTitle'),
                t('pageSummaryBasicServicesSectionLabelPriceGuaranteeContent')
              ]}
              startElement={
                <Icon iconSrc={calendarIcon} size={ICONSIZE.default} alt="calendar icon"></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.small}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />
            <TableEntry
              leftValues={[
                t('pageSummaryBasicServicesSectionLabelCustomerPortalAccessTitle'),
                t('pageSummaryBasicServicesSectionLabelCustomerPortalAccessContent')
              ]}
              startElement={
                <Icon
                  iconSrc={screenIcon}
                  size={ICONSIZE.default}
                  alt="customer portal icon"
                ></Icon>
              }
              endElement={
                <Icon
                  iconSrc={checkmarkIcon}
                  size={ICONSIZE.small}
                  centered
                  alt="checkmark icon"
                ></Icon>
              }
              additionalClassNames={isAboveXXS ? '' : 'px-0'}
            />
            <HorizontalLine />

            {additionalServicesSection()}

            <SectionSeparator />

            <SectionTitle title={t('pageSummaryPriceSectionTitle')} />

            <HorizontalLine />
            <TableEntry
              leftValues={[
                <Trans
                  i18nKey="pageSummaryPriceSectionLabelTotalPrice"
                  values={{
                    priceTerm: t('pageSummaryPriceSectionLabelPriceTerm'),
                    numberOfApartments: state.onboardingInfo!.numberOfApartments
                  }}
                />,
                <CenteredSubContent>
                  <Trans i18nKey="pageSummaryPriceSectionSublabelTotalPrice" />
                </CenteredSubContent>
              ]}
              endElement={
                <PriceTag className="m-0 fs-4 fw-semibold">
                  {formatPrice(t, sumPrice(state.onboardingInfo!.products))}
                </PriceTag>
              }
            />
            <HorizontalLine />

            <SectionSeparator />

            <Card className="w-100 mb-5 mx-0">
              <Col>
                <Row>
                  <h4 className="my-0">{t('pageSummaryWhatElseIsImportant')}</h4>
                </Row>
                <Row>
                  <p className="copy mb-0">{t('pageSummaryWhatElseIsImportantParagraph1')}</p>
                </Row>
                <Row>
                  <p className="copy mb-0">{t('pageSummaryWhatElseIsImportantParagraph2')}</p>
                </Row>
              </Col>
            </Card>

            <Card className="w-100 mb-5 mx-0">
              <h4 className="my-0">{t('pageSummaryWhatHappensNext')}</h4>
              <p className="copy mb-0">{t('pageSummaryWhatHappensNextParagraph')}</p>
            </Card>

            <Row className="justify-content-center">
              <Col xs={{ span: 12 }} className={'mb-3 text-center'}>
                <AcceptDataProcessingTermsCheckbox checked={accepted} onChange={setAccepted} />
              </Col>
              <Col
                xs={{ span: 12, order: 1 }}
                md={{ span: 6, order: 2 }}
                className="mb-3 d-flex justify-content-center"
              >
                <Button
                  disabled={!accepted}
                  size={SIZE.large}
                  onClick={() => handleOrderNowButtonClicked()}
                  isLoading={state.apiCallInProgress}
                >
                  {t('pageSummaryButtonTextOrderNow')}
                </Button>
              </Col>
            </Row>

            <SectionSeparator />
          </Col>
        </Row>
      )}
    </Container>
  );
};

export default ConfirmOfferPage;
